import { StoryFragment, TheoryDef } from '../types';

// ── Story Fragments for all 10 dig sites ──

export const STORY_FRAGMENTS: StoryFragment[] = [

  // ════════════════════════════════════════════════════
  // DUSTY CREEK — Native American trading hub
  // Story: A master potter connected distant tribes
  // ════════════════════════════════════════════════════

  {
    id: 'dc_frag_01',
    siteId: 'dusty_creek',
    text: 'The clay here bears fingerprints pressed with deliberate care, the unmistakable marks of a single artisan who shaped vessel after vessel with tireless hands.',
    order: 1,
    artifactId: 'dc_pottery_bowl',
  },
  {
    id: 'dc_frag_02',
    siteId: 'dusty_creek',
    text: 'This obsidian did not originate anywhere near this valley. It was carried hundreds of miles from volcanic peaks to the north, proof that goods flowed along established routes.',
    order: 2,
    artifactId: 'dc_arrowhead_obsidian',
    connection: 'dc_frag_05',
  },
  {
    id: 'dc_frag_03',
    siteId: 'dusty_creek',
    text: 'Worn smooth by years of use, this grinding stone processed maize from distant southern growers, a crop foreign to the local diet before the trade routes opened.',
    order: 3,
    artifactId: 'dc_grinding_stone',
  },
  {
    id: 'dc_frag_04',
    siteId: 'dusty_creek',
    text: 'A turquoise pendant carved in a style unknown to this region. The craftsmanship speaks of Southwestern desert peoples, yet it was found beside local burial goods.',
    order: 4,
    artifactId: 'dc_turquoise_pendant',
    connection: 'dc_frag_06',
  },
  {
    id: 'dc_frag_05',
    siteId: 'dusty_creek',
    text: 'Shell beads from the Pacific coast, strung on sinew alongside freshwater pearls from eastern rivers. Two oceans meeting in one necklace, joined by the potter who traded for them.',
    order: 5,
    artifactId: 'dc_shell_beads',
    connection: 'dc_frag_02',
  },
  {
    id: 'dc_frag_06',
    siteId: 'dusty_creek',
    text: 'The same potter\'s fingerprints appear on this ceremonial pipe, but the design is borrowed from plains tribes. The artist was learning, absorbing, blending traditions.',
    order: 6,
    artifactId: 'dc_ceremonial_pipe',
    connection: 'dc_frag_04',
  },
  {
    id: 'dc_frag_07',
    siteId: 'dusty_creek',
    text: 'A fire pit ringed with stones carried from four different geological regions. Whoever gathered here came from many directions, drawn to a common hearth.',
    order: 7,
    artifactId: 'dc_fire_pit_stones',
  },
  {
    id: 'dc_frag_08',
    siteId: 'dusty_creek',
    text: 'Etched into this bone awl is a crude map showing river crossings and mountain passes. It is the oldest trade route guide ever found in this region.',
    order: 8,
    artifactId: 'dc_bone_awl',
    connection: 'dc_frag_10',
  },
  {
    id: 'dc_frag_09',
    siteId: 'dusty_creek',
    text: 'A basket woven with a technique from the Great Basin, yet decorated with pigments local to this creek. Two traditions woven into one functional work of art.',
    order: 9,
    artifactId: 'dc_woven_basket',
  },
  {
    id: 'dc_frag_10',
    siteId: 'dusty_creek',
    text: 'Copper ore shaped into a small figurine of a woman carrying bundles on her back. She is the trader, the walker between worlds, and she appears on pottery from five different tribal regions.',
    order: 10,
    artifactId: 'dc_copper_figurine',
    connection: 'dc_frag_08',
  },
  {
    id: 'dc_frag_11',
    siteId: 'dusty_creek',
    text: 'Fragments of a large storage vessel show scorch marks and hasty repair. The potter refused to let it break, mending cracks with pine resin and red clay. This pot held something precious.',
    order: 11,
    artifactId: 'dc_storage_vessel',
  },
  {
    id: 'dc_frag_12',
    siteId: 'dusty_creek',
    text: 'A deer bone flute tuned to a pentatonic scale shared by tribes across the continent. Music was the universal language that drew traders to this creek each autumn.',
    order: 12,
    artifactId: 'dc_bone_flute',
  },
  {
    id: 'dc_frag_13',
    siteId: 'dusty_creek',
    text: 'Red ochre pigment stored in a hollowed stone, used to paint the distinctive spiral motifs found on the potter\'s work. The same spirals have been found in caves a thousand miles apart.',
    order: 13,
    artifactId: 'dc_ochre_stone',
  },
  {
    id: 'dc_frag_14',
    siteId: 'dusty_creek',
    text: 'The potter\'s final work: a great bowl painted with figures from every tribe that visited the creek. A pictorial census of an entire trade network, preserved in clay.',
    order: 14,
    artifactId: 'dc_painted_bowl',
    connection: 'dc_frag_01',
  },
  {
    id: 'dc_frag_15',
    siteId: 'dusty_creek',
    text: 'Buried beside the great bowl, a bundle of dried herbs wrapped in leather from the northern plains. An offering left for the potter whose work united a continent.',
    order: 15,
    artifactId: 'dc_herb_bundle',
  },

  // ════════════════════════════════════════════════════
  // MILLBROOK FARM — Roman villa, secret resistance
  // Story: A nobleman secretly aided local Britons
  // ════════════════════════════════════════════════════

  {
    id: 'mf_frag_01',
    siteId: 'millbrook_farm',
    text: 'This mosaic depicts a Roman senator in full toga, but hidden in the border tiles is a Celtic knot pattern. Someone in this household honored two allegiances.',
    order: 1,
    artifactId: 'mf_mosaic_fragment',
  },
  {
    id: 'mf_frag_02',
    siteId: 'millbrook_farm',
    text: 'A denarius bearing the face of Emperor Claudius, defaced with a tiny scratched symbol: a stag, the emblem of the local Briton chieftain.',
    order: 2,
    artifactId: 'mf_roman_coin',
    connection: 'mf_frag_05',
  },
  {
    id: 'mf_frag_03',
    siteId: 'millbrook_farm',
    text: 'A bronze stylus and wax tablet bearing Latin script. The message is mundane on the surface, but certain letters are marked with tiny dots forming a second, hidden message in Brythonic.',
    order: 3,
    artifactId: 'mf_wax_tablet',
    connection: 'mf_frag_07',
  },
  {
    id: 'mf_frag_04',
    siteId: 'millbrook_farm',
    text: 'Iron nails and hinges from a hidden door, concealed behind what was once a wine cellar wall. The villa had a secret room that doesn\'t appear on any Roman building survey.',
    order: 4,
    artifactId: 'mf_iron_hinges',
  },
  {
    id: 'mf_frag_05',
    siteId: 'millbrook_farm',
    text: 'A Samian ware bowl imported from Gaul, but its base is inscribed with a list of names in crude lettering. These are not Roman names. They are Briton tribal leaders.',
    order: 5,
    artifactId: 'mf_samian_bowl',
    connection: 'mf_frag_02',
  },
  {
    id: 'mf_frag_06',
    siteId: 'millbrook_farm',
    text: 'A Celtic torc of twisted gold, buried in haste beneath the villa\'s floor. No Roman nobleman should possess such an item, yet this one kept it close.',
    order: 6,
    artifactId: 'mf_celtic_torc',
  },
  {
    id: 'mf_frag_07',
    siteId: 'millbrook_farm',
    text: 'A second wax tablet, this one bearing instructions for a night meeting at the old oak grove. The handwriting matches the first tablet. The nobleman organized gatherings.',
    order: 7,
    artifactId: 'mf_wax_tablet_2',
    connection: 'mf_frag_03',
  },
  {
    id: 'mf_frag_08',
    siteId: 'millbrook_farm',
    text: 'An amphora of Mediterranean wine, half-consumed and sealed again with local beeswax. Roman hospitality served to Briton guests, shared in secret.',
    order: 8,
    artifactId: 'mf_amphora',
  },
  {
    id: 'mf_frag_09',
    siteId: 'millbrook_farm',
    text: 'A child\'s leather shoe, too small for Roman sandal patterns and stitched in the Briton way. A local child lived in this villa, treated as family.',
    order: 9,
    artifactId: 'mf_leather_shoe',
  },
  {
    id: 'mf_frag_10',
    siteId: 'millbrook_farm',
    text: 'Shattered pottery reassembled reveals a scene: a Roman and a Briton clasping forearms in the gesture of equals. This image would have been treasonous.',
    order: 10,
    artifactId: 'mf_pottery_scene',
    connection: 'mf_frag_01',
  },
  {
    id: 'mf_frag_11',
    siteId: 'millbrook_farm',
    text: 'A Roman military fibula brooch snapped in two and discarded. The nobleman abandoned his rank insignia, perhaps the night he chose the Britons over Rome.',
    order: 11,
    artifactId: 'mf_fibula_brooch',
  },
  {
    id: 'mf_frag_12',
    siteId: 'millbrook_farm',
    text: 'A cache of iron spearheads hidden beneath the hypocaust floor. Roman-forged weapons stockpiled for Briton hands. The nobleman was arming the resistance.',
    order: 12,
    artifactId: 'mf_spearheads',
    connection: 'mf_frag_14',
  },
  {
    id: 'mf_frag_13',
    siteId: 'millbrook_farm',
    text: 'A lead curse tablet asking the gods to protect "one who walks between two peoples." It was written by a Briton druid, asking divine favor for their Roman ally.',
    order: 13,
    artifactId: 'mf_curse_tablet',
  },
  {
    id: 'mf_frag_14',
    siteId: 'millbrook_farm',
    text: 'Burned timbers and scorched stone mark the villa\'s violent end. The Romans discovered the betrayal. The nobleman\'s final act was to warn the Britons before the legions arrived.',
    order: 14,
    artifactId: 'mf_burned_timber',
    connection: 'mf_frag_12',
  },
  {
    id: 'mf_frag_15',
    siteId: 'millbrook_farm',
    text: 'In the rubble of the collapsed secret room, a small stone altar engraved in both Latin and Brythonic: "Here stood friendship stronger than empire."',
    order: 15,
    artifactId: 'mf_stone_altar',
  },

  // ════════════════════════════════════════════════════
  // SUNKEN TEMPLE — Mayan astronomers predicted catastrophe
  // ════════════════════════════════════════════════════

  {
    id: 'st_frag_01',
    siteId: 'sunken_temple',
    text: 'This jade mask depicts Itzamna, god of wisdom, with star charts etched into the cheeks. The astronomers who built this temple worshipped knowledge above all.',
    order: 1,
    artifactId: 'st_jade_mask',
  },
  {
    id: 'st_frag_02',
    siteId: 'sunken_temple',
    text: 'An obsidian mirror polished to perfection, used to track celestial movements. Scratches on its back record a cycle of observations spanning forty years.',
    order: 2,
    artifactId: 'st_obsidian_mirror',
  },
  {
    id: 'st_frag_03',
    siteId: 'sunken_temple',
    text: 'A calendar stone fragment shows dates circled in red pigment, each one corresponding to a recorded earthquake. The astronomers were tracking seismic patterns.',
    order: 3,
    artifactId: 'st_calendar_stone',
    connection: 'st_frag_06',
  },
  {
    id: 'st_frag_04',
    siteId: 'sunken_temple',
    text: 'Carved glyphs on this lintel describe a debate between the temple astronomers and the city\'s high priest. The scientists warned of danger; the priest demanded silence.',
    order: 4,
    artifactId: 'st_carved_lintel',
    connection: 'st_frag_08',
  },
  {
    id: 'st_frag_05',
    siteId: 'sunken_temple',
    text: 'A ceramic vessel shaped like a jaguar contains traces of cacao and hallucinogenic mushrooms. The astronomers used ritual visions to guide their calculations.',
    order: 5,
    artifactId: 'st_jaguar_vessel',
  },
  {
    id: 'st_frag_06',
    siteId: 'sunken_temple',
    text: 'A second calendar stone, this one marked with a single date circled three times in black: the predicted day of catastrophe. The astronomers calculated it years in advance.',
    order: 6,
    artifactId: 'st_calendar_stone_2',
    connection: 'st_frag_03',
  },
  {
    id: 'st_frag_07',
    siteId: 'sunken_temple',
    text: 'A stucco panel shows the temple half-submerged in water, with astronomers on the roof pointing at the sky. They painted their own future destruction.',
    order: 7,
    artifactId: 'st_stucco_panel',
  },
  {
    id: 'st_frag_08',
    siteId: 'sunken_temple',
    text: 'A jade pendant bearing the name glyph of Ahau K\'in, the head astronomer. He petitioned the king three times to evacuate the city. Each petition was refused.',
    order: 8,
    artifactId: 'st_jade_pendant',
    connection: 'st_frag_04',
  },
  {
    id: 'st_frag_09',
    siteId: 'sunken_temple',
    text: 'Obsidian blades arranged in a ritual pattern around a central altar. The astronomers performed a ceremony of last resort, asking the gods to intervene where the king would not.',
    order: 9,
    artifactId: 'st_obsidian_blades',
  },
  {
    id: 'st_frag_10',
    siteId: 'sunken_temple',
    text: 'A conch shell trumpet, cracked from water pressure, once used to summon the faithful to the temple observatory. In the final days, it sounded warnings that went unheeded.',
    order: 10,
    artifactId: 'st_conch_trumpet',
  },
  {
    id: 'st_frag_11',
    siteId: 'sunken_temple',
    text: 'A clay figurine of a woman carrying children on her back, fleeing uphill. Some families listened to the astronomers and escaped before the earthquake.',
    order: 11,
    artifactId: 'st_clay_figurine',
  },
  {
    id: 'st_frag_12',
    siteId: 'sunken_temple',
    text: 'Coral has grown over a carved warning stone placed at the temple entrance. It reads: "The earth will swallow what the sky foretold. Remember us."',
    order: 12,
    artifactId: 'st_warning_stone',
    connection: 'st_frag_15',
  },
  {
    id: 'st_frag_13',
    siteId: 'sunken_temple',
    text: 'A ceramic codex holder, sealed with wax, contained a waterlogged but partially legible text: the astronomers\' complete seismic calculations, accurate to within days.',
    order: 13,
    artifactId: 'st_codex_holder',
  },
  {
    id: 'st_frag_14',
    siteId: 'sunken_temple',
    text: 'A gold ear spool depicting the sun being swallowed by the earth. The astronomers understood that their world sat on unstable ground long before anyone else did.',
    order: 14,
    artifactId: 'st_gold_ear_spool',
  },
  {
    id: 'st_frag_15',
    siteId: 'sunken_temple',
    text: 'At the temple\'s highest surviving point, a stone box held obsidian mirrors, calendar fragments, and a jade figure of Ahau K\'in. He sealed his life\'s work before the waters came.',
    order: 15,
    artifactId: 'st_stone_box',
    connection: 'st_frag_12',
  },
  {
    id: 'st_frag_16',
    siteId: 'sunken_temple',
    text: 'Barnacle-encrusted steps lead downward into darkness. The earthquake dropped the temple twelve feet in seconds, and the cenote waters rushed in to claim it.',
    order: 16,
    artifactId: 'st_temple_steps',
  },
  {
    id: 'st_frag_17',
    siteId: 'sunken_temple',
    text: 'A painted mural, half-dissolved by centuries underwater, shows Ahau K\'in teaching young apprentices to read the stars. His school of astronomy died with the temple.',
    order: 17,
    artifactId: 'st_painted_mural',
  },
  {
    id: 'st_frag_18',
    siteId: 'sunken_temple',
    text: 'A jade death mask was placed over the face of a skeleton found in the temple\'s inner sanctum. Ahau K\'in chose to stay with his observatory as the world collapsed around him.',
    order: 18,
    artifactId: 'st_death_mask',
  },

  // ════════════════════════════════════════════════════
  // SILK ROAD — Merchant who brokered peace
  // ════════════════════════════════════════════════════

  {
    id: 'sr_frag_01',
    siteId: 'silk_road',
    text: 'A silk fragment bearing Chinese characters and Persian embroidery on the same cloth. Two civilizations stitched together by commerce and mutual need.',
    order: 1,
    artifactId: 'sr_silk_fragment',
  },
  {
    id: 'sr_frag_02',
    siteId: 'silk_road',
    text: 'A Roman glass goblet found alongside a Chinese lacquer cup. They sat on the same table, served by the same host: a merchant known along the road simply as "the Bridge."',
    order: 2,
    artifactId: 'sr_roman_glass',
    connection: 'sr_frag_04',
  },
  {
    id: 'sr_frag_03',
    siteId: 'silk_road',
    text: 'Persian silver coins stamped with a merchant\'s personal mark: two hands clasped across a mountain range. This was the Bridge\'s seal, found in markets from Xi\'an to Constantinople.',
    order: 3,
    artifactId: 'sr_persian_coins',
  },
  {
    id: 'sr_frag_04',
    siteId: 'silk_road',
    text: 'A Chinese lacquer box containing letters in three languages. The Bridge kept correspondence with traders from every major civilization along the road.',
    order: 4,
    artifactId: 'sr_lacquer_box',
    connection: 'sr_frag_02',
  },
  {
    id: 'sr_frag_05',
    siteId: 'silk_road',
    text: 'A bronze scale and set of standardized weights. The Bridge created a common measurement system that both Eastern and Western merchants trusted.',
    order: 5,
    artifactId: 'sr_bronze_scales',
  },
  {
    id: 'sr_frag_06',
    siteId: 'silk_road',
    text: 'A clay tablet records a dispute between Sogdian and Kushan traders that nearly erupted into violence. A marginal note reads: "Settled by the Bridge at moonrise."',
    order: 6,
    artifactId: 'sr_clay_tablet',
    connection: 'sr_frag_09',
  },
  {
    id: 'sr_frag_07',
    siteId: 'silk_road',
    text: 'An Indian ivory comb carved with scenes of a caravan crossing the Taklamakan Desert. The Bridge funded safe passage through the most dangerous stretch of the road.',
    order: 7,
    artifactId: 'sr_ivory_comb',
  },
  {
    id: 'sr_frag_08',
    siteId: 'silk_road',
    text: 'Camel bones with healed fractures, suggesting careful veterinary attention. The Bridge maintained a stable of healthy animals and lent them to struggling caravans.',
    order: 8,
    artifactId: 'sr_camel_bones',
  },
  {
    id: 'sr_frag_09',
    siteId: 'silk_road',
    text: 'A Kushan dagger and a Sogdian dagger, both broken ceremonially and buried together. The Bridge brokered not just trade but peace, sealing treaties with ritual.',
    order: 9,
    artifactId: 'sr_broken_daggers',
    connection: 'sr_frag_06',
  },
  {
    id: 'sr_frag_10',
    siteId: 'silk_road',
    text: 'A Buddhist prayer wheel beside a Zoroastrian fire altar fragment. The waystation was a place of religious tolerance, where faith was personal and trade was universal.',
    order: 10,
    artifactId: 'sr_prayer_wheel',
  },
  {
    id: 'sr_frag_11',
    siteId: 'silk_road',
    text: 'Ceramic pipes from a sophisticated water system that fed the waystation\'s gardens. The Bridge built an oasis in the desert, a marvel of engineering that sustained hundreds.',
    order: 11,
    artifactId: 'sr_ceramic_pipes',
  },
  {
    id: 'sr_frag_12',
    siteId: 'silk_road',
    text: 'A Greek-style amphora filled with Chinese spices, sealed with an Indian wax stamp. Three civilizations in one vessel, a perfect emblem of the Bridge\'s world.',
    order: 12,
    artifactId: 'sr_spice_amphora',
  },
  {
    id: 'sr_frag_13',
    siteId: 'silk_road',
    text: 'A mosaic floor depicting the waystation at its height: flags of a dozen nations, bustling markets, and at the center, a figure with arms outstretched in welcome.',
    order: 13,
    artifactId: 'sr_mosaic_floor',
  },
  {
    id: 'sr_frag_14',
    siteId: 'silk_road',
    text: 'A sealed letter in Sogdian, addressed to "the children of the Bridge," instructs them to maintain the waystation and never turn away a traveler. The merchant\'s final testament.',
    order: 14,
    artifactId: 'sr_sogdian_letter',
    connection: 'sr_frag_15',
  },
  {
    id: 'sr_frag_15',
    siteId: 'silk_road',
    text: 'A carved stone marker at what was once the waystation\'s gate reads: "All roads lead here. All peoples are welcome. The Bridge endures." The waystation outlived its founder by two centuries.',
    order: 15,
    artifactId: 'sr_stone_marker',
    connection: 'sr_frag_14',
  },
  {
    id: 'sr_frag_16',
    siteId: 'silk_road',
    text: 'A jade seal bearing the Bridge\'s symbol was pressed into contracts across Central Asia. Merchants who carried this mark were guaranteed safe passage by warring factions alike.',
    order: 16,
    artifactId: 'sr_jade_seal',
  },
  {
    id: 'sr_frag_17',
    siteId: 'silk_road',
    text: 'A set of bronze bells, each tuned to a different note, hung at the waystation entrance. Arriving caravans would ring them in sequence, announcing their origin by melody.',
    order: 17,
    artifactId: 'sr_bronze_bells',
  },
  {
    id: 'sr_frag_18',
    siteId: 'silk_road',
    text: 'Beneath the waystation floor, a buried chest held gifts from every nation the Bridge served. Gold, jade, silk, glass, spice, and ivory, all untouched. Wealth meant nothing to this merchant; connection meant everything.',
    order: 18,
    artifactId: 'sr_buried_chest',
  },

  // ════════════════════════════════════════════════════
  // FROZEN CAIRN — Viking shieldmaiden warrior-poet
  // ════════════════════════════════════════════════════

  {
    id: 'fc_frag_01',
    siteId: 'frozen_cairn',
    text: 'A rune stone at the cairn\'s entrance names her: Sigrid Raven-tongue, shield-bearer of the north coast, speaker of verses that made warriors weep.',
    order: 1,
    artifactId: 'fc_rune_stone',
  },
  {
    id: 'fc_frag_02',
    siteId: 'frozen_cairn',
    text: 'An iron shield boss, dented from a dozen battles. The permafrost preserved leather fragments showing the shield was painted with a raven in flight.',
    order: 2,
    artifactId: 'fc_shield_boss',
  },
  {
    id: 'fc_frag_03',
    siteId: 'frozen_cairn',
    text: 'A sword with a pattern-welded blade, its hilt wrapped in silver wire. Runes along the fuller read: "I sing before I strike. I mourn after."',
    order: 3,
    artifactId: 'fc_pattern_sword',
    connection: 'fc_frag_07',
  },
  {
    id: 'fc_frag_04',
    siteId: 'frozen_cairn',
    text: 'A whalebone plaque carved with scenes of a woman standing before a longship, arm raised, reciting what appears to be a saga to assembled warriors.',
    order: 4,
    artifactId: 'fc_whalebone_plaque',
  },
  {
    id: 'fc_frag_05',
    siteId: 'frozen_cairn',
    text: 'A bronze brooch in the shape of Yggdrasil, the world tree. Sigrid wore this to fasten her cloak, and it bears teeth marks from a near-fatal wolf encounter.',
    order: 5,
    artifactId: 'fc_bronze_brooch',
  },
  {
    id: 'fc_frag_06',
    siteId: 'frozen_cairn',
    text: 'A second rune stone describes a battle at a river ford. Sigrid held the crossing alone for an hour while her people retreated to safety, composing a death-song as she fought.',
    order: 6,
    artifactId: 'fc_rune_stone_2',
    connection: 'fc_frag_03',
  },
  {
    id: 'fc_frag_07',
    siteId: 'frozen_cairn',
    text: 'A broken spearhead lodged in the shield boss tells of the river ford battle. She survived that day, but the wound in her shoulder never fully healed.',
    order: 7,
    artifactId: 'fc_spearhead',
    connection: 'fc_frag_03',
  },
  {
    id: 'fc_frag_08',
    siteId: 'frozen_cairn',
    text: 'An amber amulet containing a preserved insect, worn as a charm against the sea\'s fury. Sigrid sailed as far as Iceland and returned with stories that became legends.',
    order: 8,
    artifactId: 'fc_amber_amulet',
  },
  {
    id: 'dc_frag_09b',
    siteId: 'frozen_cairn',
    text: 'A bone comb etched with a verse in elder futhark: "The tide takes the brave and the craven alike. Only the song survives the sea."',
    order: 9,
    artifactId: 'fc_bone_comb',
  },
  {
    id: 'fc_frag_10',
    siteId: 'frozen_cairn',
    text: 'Iron rivets and tar-sealed planks from a small boat, buried beside her. In death, Sigrid was given a vessel to sail to Valhalla.',
    order: 10,
    artifactId: 'fc_ship_rivets',
  },
  {
    id: 'fc_frag_11',
    siteId: 'frozen_cairn',
    text: 'A silver arm ring given to loyal warriors by their jarl. Sigrid\'s bears an extra inscription: "For the voice that rallied us when hope was gone."',
    order: 11,
    artifactId: 'fc_silver_arm_ring',
  },
  {
    id: 'fc_frag_12',
    siteId: 'frozen_cairn',
    text: 'A soapstone mold for casting Thor\'s hammer pendants. Sigrid made these for her shield-companions, each one inscribed with a personal verse.',
    order: 12,
    artifactId: 'fc_soapstone_mold',
  },
  {
    id: 'fc_frag_13',
    siteId: 'frozen_cairn',
    text: 'A third rune stone, the longest, recounts Sigrid\'s final battle defending her village from raiders. She sang her greatest poem as she fell, and her people carved every word into stone.',
    order: 13,
    artifactId: 'fc_rune_stone_3',
    connection: 'fc_frag_15',
  },
  {
    id: 'fc_frag_14',
    siteId: 'frozen_cairn',
    text: 'Glass beads from a necklace, traded from as far as Byzantium. Even in the frozen north, Sigrid collected beauty from the wider world.',
    order: 14,
    artifactId: 'fc_glass_beads',
  },
  {
    id: 'fc_frag_15',
    siteId: 'frozen_cairn',
    text: 'Beside her sword and shield, a small wooden box held a stylus and blank rune staves. Even in death, Sigrid was equipped to compose. Her people believed she would sing in Odin\'s hall forever.',
    order: 15,
    artifactId: 'fc_rune_staves',
    connection: 'fc_frag_13',
  },

  // ════════════════════════════════════════════════════
  // PHARAOH'S SHADOW — Forgotten pharaoh's adviser
  // Story: The adviser preserved forbidden knowledge
  // ════════════════════════════════════════════════════

  {
    id: 'ps_frag_01',
    siteId: 'pharaohs_shadow',
    text: 'A limestone ushabti figure bears the name Khentkaus, Royal Adviser to the Forgotten Throne. No pharaoh by that epithet appears in any known king list.',
    order: 1,
    artifactId: 'ps_ushabti',
  },
  {
    id: 'ps_frag_02',
    siteId: 'pharaohs_shadow',
    text: 'A scarab seal of lapis lazuli, its underside carved with astronomical symbols unknown to standard Egyptian iconography. Khentkaus studied the stars in ways the priesthood forbade.',
    order: 2,
    artifactId: 'ps_scarab_seal',
    connection: 'ps_frag_05',
  },
  {
    id: 'ps_frag_03',
    siteId: 'pharaohs_shadow',
    text: 'Canopic jars with unusual markings suggest Khentkaus requested a non-standard burial. The organs were preserved alongside papyrus scrolls rather than the traditional amulets.',
    order: 3,
    artifactId: 'ps_canopic_jars',
  },
  {
    id: 'ps_frag_04',
    siteId: 'pharaohs_shadow',
    text: 'A hieroglyphic tablet details a court trial. Khentkaus was accused of "speaking the words that unmake the gods." The adviser was tried for heresy and acquitted by the pharaoh personally.',
    order: 4,
    artifactId: 'ps_hieroglyphic_tablet',
    connection: 'ps_frag_08',
  },
  {
    id: 'ps_frag_05',
    siteId: 'pharaohs_shadow',
    text: 'A bronze astronomical instrument of startling precision, capable of tracking lunar cycles and predicting eclipses. Khentkaus built tools that wouldn\'t be reinvented for a thousand years.',
    order: 5,
    artifactId: 'ps_bronze_instrument',
    connection: 'ps_frag_02',
  },
  {
    id: 'ps_frag_06',
    siteId: 'pharaohs_shadow',
    text: 'A gold pectoral depicting not Osiris or Ra but a figure surrounded by mathematical symbols. Khentkaus worshipped knowledge itself as divine.',
    order: 6,
    artifactId: 'ps_gold_pectoral',
  },
  {
    id: 'ps_frag_07',
    siteId: 'pharaohs_shadow',
    text: 'A false wall in the tomb conceals a chamber packed with sealed clay jars. Each jar contains a papyrus scroll covering a different branch of knowledge: medicine, engineering, agriculture, astronomy.',
    order: 7,
    artifactId: 'ps_sealed_jars',
    connection: 'ps_frag_10',
  },
  {
    id: 'ps_frag_08',
    siteId: 'pharaohs_shadow',
    text: 'A second hieroglyphic tablet reveals the pharaoh\'s name was deliberately chiseled away after his death. His successor erased him from history, but Khentkaus survived in shadow.',
    order: 8,
    artifactId: 'ps_hieroglyphic_tablet_2',
    connection: 'ps_frag_04',
  },
  {
    id: 'ps_frag_09',
    siteId: 'pharaohs_shadow',
    text: 'An alabaster lamp shaped like a lotus flower, its oil reservoir designed to burn for days. Khentkaus worked through the night, copying scrolls by lamplight before the priests could burn them.',
    order: 9,
    artifactId: 'ps_alabaster_lamp',
  },
  {
    id: 'ps_frag_10',
    siteId: 'pharaohs_shadow',
    text: 'Among the hidden scrolls, one describes surgical techniques unknown to Egyptian medicine. Khentkaus gathered knowledge from Nubia, Punt, and lands beyond the sea.',
    order: 10,
    artifactId: 'ps_medical_scroll',
    connection: 'ps_frag_07',
  },
  {
    id: 'ps_frag_11',
    siteId: 'pharaohs_shadow',
    text: 'A faience amulet of Thoth, god of writing, worn smooth by constant handling. Khentkaus prayed to the patron of scribes for strength to preserve what others sought to destroy.',
    order: 11,
    artifactId: 'ps_thoth_amulet',
  },
  {
    id: 'ps_frag_12',
    siteId: 'pharaohs_shadow',
    text: 'A copper mirror with geometric proofs scratched into its back. These proofs predate Greek mathematics by centuries, preserved only because Khentkaus hid them here.',
    order: 12,
    artifactId: 'ps_copper_mirror',
  },
  {
    id: 'ps_frag_13',
    siteId: 'pharaohs_shadow',
    text: 'The tomb\'s ceiling is painted with a star map of extraordinary accuracy, showing constellations and planetary positions that modern software confirms are correct for the period.',
    order: 13,
    artifactId: 'ps_star_map',
  },
  {
    id: 'ps_frag_14',
    siteId: 'pharaohs_shadow',
    text: 'A sandstone stela found near the entrance reads: "I, Khentkaus, have gathered what the fearful would scatter. Let the one who opens this tomb carry the light forward."',
    order: 14,
    artifactId: 'ps_sandstone_stela',
  },
  {
    id: 'ps_frag_15',
    siteId: 'pharaohs_shadow',
    text: 'A golden ankh placed in Khentkaus\'s hands holds a tiny compartment containing a seed. Even in death, the adviser planted the hope that knowledge would bloom again.',
    order: 15,
    artifactId: 'ps_golden_ankh',
  },
  {
    id: 'ps_frag_16',
    siteId: 'pharaohs_shadow',
    text: 'Beneath the sarcophagus, a final hidden scroll contains Khentkaus\'s personal testament: a plea to future generations to value truth over power, knowledge over fear.',
    order: 16,
    artifactId: 'ps_personal_scroll',
  },
  {
    id: 'ps_frag_17',
    siteId: 'pharaohs_shadow',
    text: 'A set of carved game pieces for senet, but the board is inscribed with a cipher. When decoded, it reveals the location of three other hidden knowledge caches across Egypt.',
    order: 17,
    artifactId: 'ps_senet_set',
  },
  {
    id: 'ps_frag_18',
    siteId: 'pharaohs_shadow',
    text: 'Clay seals on the hidden chamber bear the impression of Khentkaus\'s ring: a serpent swallowing its own tail, the ouroboros, symbol of eternal cycles. The adviser believed knowledge, once created, could never truly die.',
    order: 18,
    artifactId: 'ps_clay_seals',
  },
  {
    id: 'ps_frag_19',
    siteId: 'pharaohs_shadow',
    text: 'A wooden headrest carved with scenes of scribes at work, placed beneath the mummy\'s head. In the afterlife, Khentkaus intended to keep writing.',
    order: 19,
    artifactId: 'ps_wooden_headrest',
  },
  {
    id: 'ps_frag_20',
    siteId: 'pharaohs_shadow',
    text: 'A jeweled collar of gold and carnelian, a gift from the forgotten pharaoh to his most trusted adviser. The inscription reads: "You are my memory when I am dust."',
    order: 20,
    artifactId: 'ps_jeweled_collar',
  },

  // ════════════════════════════════════════════════════
  // OBSIDIAN FORGE — Samurai-era smith hid messages in blades
  // ════════════════════════════════════════════════════

  {
    id: 'of_frag_01',
    siteId: 'obsidian_forge',
    text: 'The forge\'s main anvil still bears the stamp of master smith Kanemitsu, whose blades were prized by the shogunate. But the anvil has a hidden compartment.',
    order: 1,
    artifactId: 'of_anvil',
  },
  {
    id: 'of_frag_02',
    siteId: 'obsidian_forge',
    text: 'A katana blade fragment, snapped ritually, shows microscopic characters etched into the hamon line. Kanemitsu hid messages where only a fellow smith would think to look.',
    order: 2,
    artifactId: 'of_katana_fragment',
    connection: 'of_frag_06',
  },
  {
    id: 'of_frag_03',
    siteId: 'obsidian_forge',
    text: 'A bronze temple bell, cracked and discarded, bears a casting date that corresponds with the year a powerful daimyo began planning his betrayal of the emperor.',
    order: 3,
    artifactId: 'of_bronze_bell',
  },
  {
    id: 'of_frag_04',
    siteId: 'obsidian_forge',
    text: 'A ceramic tea bowl in the wabi-sabi style, repaired with gold lacquer. Kanemitsu hosted tea ceremonies where samurai whispered of the daimyo\'s treachery.',
    order: 4,
    artifactId: 'of_tea_bowl',
  },
  {
    id: 'of_frag_05',
    siteId: 'obsidian_forge',
    text: 'Lacquered wooden armor plates bearing Kanemitsu\'s forge mark. He made armor for the loyalists, ensuring it was stronger than the traitor\'s supply.',
    order: 5,
    artifactId: 'of_armor_plates',
    connection: 'of_frag_09',
  },
  {
    id: 'of_frag_06',
    siteId: 'obsidian_forge',
    text: 'A second blade fragment reveals more hidden text. Together with the first, they spell a warning: "The crane lord turns his back on the chrysanthemum throne."',
    order: 6,
    artifactId: 'of_katana_fragment_2',
    connection: 'of_frag_02',
  },
  {
    id: 'of_frag_07',
    siteId: 'obsidian_forge',
    text: 'An iron tsuba sword guard depicting a scene of cranes and chrysanthemums. The crane was the daimyo\'s crest; the chrysanthemum, the emperor\'s. The guard tells the whole story in metal.',
    order: 7,
    artifactId: 'of_iron_tsuba',
  },
  {
    id: 'of_frag_08',
    siteId: 'obsidian_forge',
    text: 'A scroll of forge records, written in Kanemitsu\'s hand, lists every blade delivered to the traitor daimyo. Each entry has a tiny mark indicating the hidden message it carries.',
    order: 8,
    artifactId: 'of_forge_scroll',
  },
  {
    id: 'of_frag_09',
    siteId: 'obsidian_forge',
    text: 'A ceramic figurine of a fox, the trickster spirit. Kanemitsu placed these throughout the forge as a private joke: he was the fox in the daimyo\'s henhouse.',
    order: 9,
    artifactId: 'of_fox_figurine',
    connection: 'of_frag_05',
  },
  {
    id: 'of_frag_10',
    siteId: 'obsidian_forge',
    text: 'Charcoal and slag from the forge\'s final firing. Kanemitsu worked day and night in his last weeks, embedding warnings in every blade before the betrayal could unfold.',
    order: 10,
    artifactId: 'of_forge_slag',
  },
  {
    id: 'of_frag_11',
    siteId: 'obsidian_forge',
    text: 'A small Shinto shrine within the forge holds a mirror of polished steel. Behind the mirror, a compartment contains a letter naming every conspirator in the daimyo\'s plot.',
    order: 11,
    artifactId: 'of_shinto_mirror',
    connection: 'of_frag_14',
  },
  {
    id: 'of_frag_12',
    siteId: 'obsidian_forge',
    text: 'A wakizashi short sword, Kanemitsu\'s personal weapon, never delivered to any patron. Its inscription reads: "Truth is the steel that does not bend."',
    order: 12,
    artifactId: 'of_wakizashi',
  },
  {
    id: 'of_frag_13',
    siteId: 'obsidian_forge',
    text: 'An ink stone and brush set, hidden beneath the forge floor. Kanemitsu was not only a smith but a calligrapher, and he wrote secret reports to the emperor\'s loyalists.',
    order: 13,
    artifactId: 'of_ink_stone',
  },
  {
    id: 'of_frag_14',
    siteId: 'obsidian_forge',
    text: 'The forge was burned to the ground, but Kanemitsu\'s warnings reached the emperor in time. The betrayal was thwarted, the conspirators arrested, and the smith was honored posthumously.',
    order: 14,
    artifactId: 'of_burned_beams',
    connection: 'of_frag_11',
  },
  {
    id: 'of_frag_15',
    siteId: 'obsidian_forge',
    text: 'A stone tablet erected after the forge\'s destruction reads: "Here worked Kanemitsu, whose blades spoke truth when men could not. The empire stands because one smith chose honor over safety."',
    order: 15,
    artifactId: 'of_stone_tablet',
  },
  {
    id: 'of_frag_16',
    siteId: 'obsidian_forge',
    text: 'A set of hammers and tongs, Kanemitsu\'s personal tools, were recovered from the ashes and enshrined by his apprentices. Each tool bears the fox mark.',
    order: 16,
    artifactId: 'of_smith_tools',
  },
  {
    id: 'of_frag_17',
    siteId: 'obsidian_forge',
    text: 'Clay molds for casting bronze fittings reveal that Kanemitsu experimented with metallurgy far beyond his era. He alloyed metals in combinations not rediscovered until the modern age.',
    order: 17,
    artifactId: 'of_clay_molds',
  },
  {
    id: 'of_frag_18',
    siteId: 'obsidian_forge',
    text: 'A jade netsuke carved as a raven, worn by Kanemitsu as a toggle on his obi. Ravens were messengers in Japanese folklore, and Kanemitsu saw himself as a messenger of truth.',
    order: 18,
    artifactId: 'of_jade_netsuke',
  },
  {
    id: 'of_frag_19',
    siteId: 'obsidian_forge',
    text: 'Among the ruins, a sealed iron box contained Kanemitsu\'s masterwork: a tanto dagger so perfectly crafted that its blade still holds an edge after centuries. Etched along its spine is the smith\'s final message: "The forge is cold, but the truth burns on."',
    order: 19,
    artifactId: 'of_masterwork_tanto',
  },
  {
    id: 'of_frag_20',
    siteId: 'obsidian_forge',
    text: 'A collection of iron sand samples from rivers across Japan, each labeled in Kanemitsu\'s hand. The master smith traveled the country collecting the finest materials, and along the way, he collected intelligence.',
    order: 20,
    artifactId: 'of_iron_sand',
  },

  // ════════════════════════════════════════════════════
  // ATLANTEAN SHELF — Advanced city destroyed by hubris
  // ════════════════════════════════════════════════════

  {
    id: 'as_frag_01',
    siteId: 'atlantean_shelf',
    text: 'A marble column fragment bears inscriptions in an alphabet that borrows from Greek, Phoenician, and a third unknown script. This city drew knowledge from every shore.',
    order: 1,
    artifactId: 'as_marble_column',
  },
  {
    id: 'as_frag_02',
    siteId: 'atlantean_shelf',
    text: 'A bronze mechanism of interlocking gears, more complex than the Antikythera device. It calculated tidal patterns with uncanny accuracy, a tool for a city that lived by the sea.',
    order: 2,
    artifactId: 'as_bronze_mechanism',
    connection: 'as_frag_06',
  },
  {
    id: 'as_frag_03',
    siteId: 'atlantean_shelf',
    text: 'An amphora sealed with lead, still containing wine preserved by an unknown chemical process. The city\'s chemists understood preservation techniques lost for millennia.',
    order: 3,
    artifactId: 'as_sealed_amphora',
  },
  {
    id: 'as_frag_04',
    siteId: 'atlantean_shelf',
    text: 'A golden trident mounted on a pedestal, symbol of the city\'s dominion over the sea. Inscriptions boast that the waves themselves obeyed the city\'s engineers.',
    order: 4,
    artifactId: 'as_golden_trident',
  },
  {
    id: 'as_frag_05',
    siteId: 'atlantean_shelf',
    text: 'Terracotta pipes of astonishing uniformity carried heated water throughout the city. Central heating, running water, and sewage systems served even the poorest quarters.',
    order: 5,
    artifactId: 'as_terracotta_pipes',
  },
  {
    id: 'as_frag_06',
    siteId: 'atlantean_shelf',
    text: 'A second mechanism, this one designed to predict earthquakes based on water pressure changes. The engineers knew their coast was unstable, yet they built ever larger.',
    order: 6,
    artifactId: 'as_quake_device',
    connection: 'as_frag_02',
  },
  {
    id: 'as_frag_07',
    siteId: 'atlantean_shelf',
    text: 'A mosaic floor depicts the city from above: concentric rings of water and land, harbors bristling with ships, and a great lighthouse at the center that never went dark.',
    order: 7,
    artifactId: 'as_city_mosaic',
  },
  {
    id: 'as_frag_08',
    siteId: 'atlantean_shelf',
    text: 'A marble tablet records a council debate. Engineers warned that expanding the harbor would weaken the seawall. The merchant council overruled them, demanding more berths for trade ships.',
    order: 8,
    artifactId: 'as_council_tablet',
    connection: 'as_frag_12',
  },
  {
    id: 'as_frag_09',
    siteId: 'atlantean_shelf',
    text: 'A child\'s toy: a miniature bronze ship with working rudder and sails. Even the city\'s children were raised to revere the sea and the commerce it brought.',
    order: 9,
    artifactId: 'as_toy_ship',
  },
  {
    id: 'as_frag_10',
    siteId: 'atlantean_shelf',
    text: 'A statue of a woman holding scales and a compass, the city\'s patron figure representing the balance between ambition and wisdom. Her scales were found tipped toward ambition.',
    order: 10,
    artifactId: 'as_patron_statue',
  },
  {
    id: 'as_frag_11',
    siteId: 'atlantean_shelf',
    text: 'Lead ingots stamped with the city\'s seal have been found in ports across the Mediterranean. This city traded with everyone and dominated no one, until greed changed the equation.',
    order: 11,
    artifactId: 'as_lead_ingots',
  },
  {
    id: 'as_frag_12',
    siteId: 'atlantean_shelf',
    text: 'A second council tablet, dated years after the first, records the seawall\'s first major breach. The council voted to repair it cheaply rather than rebuild. The engineers resigned in protest.',
    order: 12,
    artifactId: 'as_council_tablet_2',
    connection: 'as_frag_08',
  },
  {
    id: 'as_frag_13',
    siteId: 'atlantean_shelf',
    text: 'A crystal lens of remarkable clarity, ground to magnify distant objects. The city\'s astronomers used these to study the stars, but in the end, they should have watched the sea.',
    order: 13,
    artifactId: 'as_crystal_lens',
  },
  {
    id: 'as_frag_14',
    siteId: 'atlantean_shelf',
    text: 'Gold coins minted in the city\'s final decade show the lighthouse surrounded by waves reaching its midpoint. The artists knew. The merchants refused to see.',
    order: 14,
    artifactId: 'as_gold_coins',
  },
  {
    id: 'as_frag_15',
    siteId: 'atlantean_shelf',
    text: 'A sealed bronze cylinder contains the head engineer\'s final letter: "We built a city that could tame the sea, then tore down the walls that kept the sea at bay. Forgive us."',
    order: 15,
    artifactId: 'as_bronze_cylinder',
    connection: 'as_frag_17',
  },
  {
    id: 'as_frag_16',
    siteId: 'atlantean_shelf',
    text: 'An anchor of unusual design, capable of holding a ship in the fiercest storm. The city\'s maritime engineering was centuries ahead, but no anchor could hold back the tide of folly.',
    order: 16,
    artifactId: 'as_anchor',
  },
  {
    id: 'as_frag_17',
    siteId: 'atlantean_shelf',
    text: 'The lighthouse foundation, still standing on the seabed, shows where the final breach occurred. The sea entered through the expanded harbor, exactly as the engineers predicted.',
    order: 17,
    artifactId: 'as_lighthouse_base',
    connection: 'as_frag_15',
  },
  {
    id: 'as_frag_18',
    siteId: 'atlantean_shelf',
    text: 'A marble hand reaching upward from the rubble, all that remains of a colossal statue that once stood at the harbor entrance. The city reached for greatness and grasped too far.',
    order: 18,
    artifactId: 'as_marble_hand',
  },
  {
    id: 'as_frag_19',
    siteId: 'atlantean_shelf',
    text: 'Scattered across the seabed, thousands of mosaic tiles from the great library floor. An entire civilization\'s written knowledge, reduced to decorative rubble by the weight of the ocean.',
    order: 19,
    artifactId: 'as_mosaic_tiles',
  },
  {
    id: 'as_frag_20',
    siteId: 'atlantean_shelf',
    text: 'A bronze plaque, barnacle-covered but legible, once hung above the city gates. It reads: "We are the masters of wave and stone." The sea, apparently, disagreed.',
    order: 20,
    artifactId: 'as_bronze_plaque',
  },

  // ════════════════════════════════════════════════════
  // LIBRARY OF SANDS — Lost civilization's final warning
  // ════════════════════════════════════════════════════

  {
    id: 'ls_frag_01',
    siteId: 'library_of_sands',
    text: 'A sandstone tablet covered in a script that resembles no known writing system. The symbols are geometric, precise, and arranged in spiraling columns rather than lines.',
    order: 1,
    artifactId: 'ls_sandstone_tablet',
  },
  {
    id: 'ls_frag_02',
    siteId: 'library_of_sands',
    text: 'A bronze astrolabe of extraordinary complexity, with calibrations for stars that have since shifted position. This instrument is older than any known astrolabe by centuries.',
    order: 2,
    artifactId: 'ls_bronze_astrolabe',
  },
  {
    id: 'ls_frag_03',
    siteId: 'library_of_sands',
    text: 'Clay cylinders stacked in honeycomb formations once filled this chamber. Each cylinder held a scroll. This was a library organized with a cataloging system of bewildering sophistication.',
    order: 3,
    artifactId: 'ls_clay_cylinders',
  },
  {
    id: 'ls_frag_04',
    siteId: 'library_of_sands',
    text: 'A quartz lens set in a bronze frame, used to read micro-inscriptions invisible to the naked eye. These people wrote entire texts on surfaces the size of a thumbnail.',
    order: 4,
    artifactId: 'ls_quartz_lens',
    connection: 'ls_frag_08',
  },
  {
    id: 'ls_frag_05',
    siteId: 'library_of_sands',
    text: 'An obsidian slab etched with a map showing coastlines that match no modern geography. Either the map is wrong, or the world\'s coasts have changed more than we believed.',
    order: 5,
    artifactId: 'ls_obsidian_map',
  },
  {
    id: 'ls_frag_06',
    siteId: 'library_of_sands',
    text: 'A set of gold measuring rods calibrated to a unit of length that is a precise fraction of the Earth\'s circumference. These people knew the planet\'s dimensions.',
    order: 6,
    artifactId: 'ls_gold_rods',
  },
  {
    id: 'ls_frag_07',
    siteId: 'library_of_sands',
    text: 'A ceramic jar contains seeds preserved in honey, still viable after millennia. The library preserved not just knowledge but life itself, a seed bank against catastrophe.',
    order: 7,
    artifactId: 'ls_seed_jar',
  },
  {
    id: 'ls_frag_08',
    siteId: 'library_of_sands',
    text: 'Using the quartz lens on the sandstone tablets reveals a second layer of text: instructions for decoding the spiral script. They wanted to be understood, but only by those patient enough to look closely.',
    order: 8,
    artifactId: 'ls_micro_tablet',
    connection: 'ls_frag_04',
  },
  {
    id: 'ls_frag_09',
    siteId: 'library_of_sands',
    text: 'A copper plate engraved with anatomical diagrams of the human body, including accurate depictions of the circulatory system. Medical knowledge of this caliber wouldn\'t resurface until the Renaissance.',
    order: 9,
    artifactId: 'ls_copper_plate',
  },
  {
    id: 'ls_frag_10',
    siteId: 'library_of_sands',
    text: 'A stone basin filled with crystallized mercury, once used as a reflective surface for astronomical observations. The library doubled as an observatory.',
    order: 10,
    artifactId: 'ls_mercury_basin',
  },
  {
    id: 'ls_frag_11',
    siteId: 'library_of_sands',
    text: 'Decoding more of the spiral script reveals repeated references to "the drying." The civilization tracked their climate changing over generations, recording rainfall data with scientific rigor.',
    order: 11,
    artifactId: 'ls_climate_tablet',
    connection: 'ls_frag_15',
  },
  {
    id: 'ls_frag_12',
    siteId: 'library_of_sands',
    text: 'A jade figurine of a robed figure holding a scroll and pointing toward the horizon. The librarians were also travelers, sent out to gather knowledge from distant peoples.',
    order: 12,
    artifactId: 'ls_jade_librarian',
  },
  {
    id: 'ls_frag_13',
    siteId: 'library_of_sands',
    text: 'A mosaic floor depicts the library surrounded by fertile grassland and rivers. This desert was once green, and the library was built at the heart of a thriving civilization.',
    order: 13,
    artifactId: 'ls_mosaic_landscape',
  },
  {
    id: 'ls_frag_14',
    siteId: 'library_of_sands',
    text: 'Iron tools designed for digging deep wells, found in a storage chamber. As the water table dropped, the civilization fought to survive. The library records their increasingly desperate innovations.',
    order: 14,
    artifactId: 'ls_well_tools',
  },
  {
    id: 'ls_frag_15',
    siteId: 'library_of_sands',
    text: 'The final climate tablets show rainfall dropping to zero over a thirty-year period. The civilization saw its end approaching and chose to spend its remaining resources preserving knowledge rather than fighting the inevitable.',
    order: 15,
    artifactId: 'ls_final_climate',
    connection: 'ls_frag_11',
  },
  {
    id: 'ls_frag_16',
    siteId: 'library_of_sands',
    text: 'A carved stone door, sealed with a mechanism that required solving a mathematical puzzle to open. The library was locked against the ignorant and the hasty.',
    order: 16,
    artifactId: 'ls_puzzle_door',
  },
  {
    id: 'ls_frag_17',
    siteId: 'library_of_sands',
    text: 'A silver compass that does not point north but instead aligns with the library\'s central chamber. Other compasses found at distant sites point the same direction, a network guiding seekers here.',
    order: 17,
    artifactId: 'ls_silver_compass',
  },
  {
    id: 'ls_frag_18',
    siteId: 'library_of_sands',
    text: 'The innermost chamber holds a single large tablet, the library\'s final message. The decoded text reads: "We perished because the world changed and we could not change it back. Learn from this. Adapt, or leave a library for those who come after."',
    order: 18,
    artifactId: 'ls_final_tablet',
  },
  {
    id: 'ls_frag_19',
    siteId: 'library_of_sands',
    text: 'A ceramic model of the library itself, showing three underground levels that remain unexcavated. The knowledge we\'ve found may be only a fraction of what was stored here.',
    order: 19,
    artifactId: 'ls_library_model',
  },
  {
    id: 'ls_frag_20',
    siteId: 'library_of_sands',
    text: 'Carved into the library\'s outer wall, facing the desert, a line of symbols translates to a single repeating word: "Remember. Remember. Remember." A civilization\'s dying wish, etched in stone.',
    order: 20,
    artifactId: 'ls_outer_wall',
  },

  // ════════════════════════════════════════════════════
  // GENESIS CAVERN — Proto-humans, art and ritual
  // ════════════════════════════════════════════════════

  {
    id: 'gc_frag_01',
    siteId: 'genesis_cavern',
    text: 'A hand axe of remarkable symmetry, shaped well beyond functional need. Whoever made this cared about beauty. The aesthetic impulse is older than we imagined.',
    order: 1,
    artifactId: 'gc_hand_axe',
  },
  {
    id: 'gc_frag_02',
    siteId: 'genesis_cavern',
    text: 'Red ochre pigment ground to a fine powder and stored in a tortoise shell. Paint-making, a deliberate multi-step process, performed by beings we assumed incapable of such planning.',
    order: 2,
    artifactId: 'gc_ochre_shell',
    connection: 'gc_frag_05',
  },
  {
    id: 'gc_frag_03',
    siteId: 'genesis_cavern',
    text: 'A bone flute with five holes, tuned to a pentatonic scale. Music existed hundreds of thousands of years earlier than any previous evidence suggests. The cavern echoes with ghost melodies.',
    order: 3,
    artifactId: 'gc_bone_flute',
  },
  {
    id: 'gc_frag_04',
    siteId: 'genesis_cavern',
    text: 'A hearth ringed with deliberately placed stones, each a different color. The fire pit was decorative, arranged with intent. These proto-humans curated their living space.',
    order: 4,
    artifactId: 'gc_hearth_stones',
  },
  {
    id: 'gc_frag_05',
    siteId: 'genesis_cavern',
    text: 'On the cavern wall, handprints in red ochre surround a crude but unmistakable image of a bison. This is cave art predating all known examples by an order of magnitude.',
    order: 5,
    artifactId: 'gc_cave_painting',
    connection: 'gc_frag_02',
  },
  {
    id: 'gc_frag_06',
    siteId: 'genesis_cavern',
    text: 'A collection of perforated seashells, strung together as a necklace. The shells came from a coast over a hundred miles away. These beings traveled, traded, or both.',
    order: 6,
    artifactId: 'gc_shell_necklace',
  },
  {
    id: 'gc_frag_07',
    siteId: 'genesis_cavern',
    text: 'A child\'s skeleton buried in a flexed position, surrounded by flowers preserved as pollen traces. Ritual burial, grief, reverence for the dead, all present impossibly early.',
    order: 7,
    artifactId: 'gc_child_burial',
    connection: 'gc_frag_11',
  },
  {
    id: 'gc_frag_08',
    siteId: 'genesis_cavern',
    text: 'Scoring marks on a flat stone form a tally system. Twenty-eight marks in a row, then a break, then twenty-eight more. Someone was counting lunar cycles.',
    order: 8,
    artifactId: 'gc_tally_stone',
  },
  {
    id: 'gc_frag_09',
    siteId: 'genesis_cavern',
    text: 'A carved ivory figurine, small enough to fit in a palm, depicting a human form with exaggerated features. Abstract representation of the self, a philosophical leap of staggering implication.',
    order: 9,
    artifactId: 'gc_ivory_figurine',
  },
  {
    id: 'gc_frag_10',
    siteId: 'genesis_cavern',
    text: 'Stone tools arranged in a teaching pattern: rough attempts beside expert examples, progressing from clumsy to skilled. This was a classroom. Knowledge was being transmitted deliberately.',
    order: 10,
    artifactId: 'gc_teaching_tools',
  },
  {
    id: 'gc_frag_11',
    siteId: 'genesis_cavern',
    text: 'An adult skeleton beside the child, positioned protectively, also buried with care. Two individuals mourned together. The concept of family transcends all timelines we had assumed.',
    order: 11,
    artifactId: 'gc_adult_burial',
    connection: 'gc_frag_07',
  },
  {
    id: 'gc_frag_12',
    siteId: 'genesis_cavern',
    text: 'A grinding stone used not for food but for pigment. The amount of ochre processed here far exceeds what painting alone would require. They were decorating their bodies, their tools, their world.',
    order: 12,
    artifactId: 'gc_pigment_grinder',
  },
  {
    id: 'gc_frag_13',
    siteId: 'genesis_cavern',
    text: 'Deep in the cavern, beyond the living area, a chamber contains only art. No tools, no bones, no fire pits. This was a gallery, a sacred space set apart from daily life.',
    order: 13,
    artifactId: 'gc_gallery_stones',
  },
  {
    id: 'gc_frag_14',
    siteId: 'genesis_cavern',
    text: 'A staff of polished bone, too fragile for practical use, decorated with carved spirals. A ceremonial object. Ritual, symbolism, and social hierarchy existed at the dawn of our species.',
    order: 14,
    artifactId: 'gc_bone_staff',
  },
  {
    id: 'gc_frag_15',
    siteId: 'genesis_cavern',
    text: 'The gallery chamber\'s ceiling bears the largest painting: a night sky with dots arranged to match the visible constellations. Proto-humans looked up and mapped the heavens before they mapped the earth.',
    order: 15,
    artifactId: 'gc_star_painting',
  },
  {
    id: 'gc_frag_16',
    siteId: 'genesis_cavern',
    text: 'A quartz crystal, naturally occurring but deliberately collected and placed at the center of the gallery. It catches any light and scatters it across the painted walls. They understood optics intuitively.',
    order: 16,
    artifactId: 'gc_quartz_crystal',
  },
  {
    id: 'gc_frag_17',
    siteId: 'genesis_cavern',
    text: 'Pollen analysis reveals that specific plants were brought into the cavern seasonally for hundreds of years. The same species, the same pattern. A tradition maintained across generations.',
    order: 17,
    artifactId: 'gc_pollen_samples',
  },
  {
    id: 'gc_frag_18',
    siteId: 'genesis_cavern',
    text: 'At the cavern\'s deepest point, a final handprint in red ochre, pressed firmly into the stone. Beside it, scratched with a sharp tool, a mark that might be a name. The first signature in human history.',
    order: 18,
    artifactId: 'gc_final_handprint',
  },
  {
    id: 'gc_frag_19',
    siteId: 'genesis_cavern',
    text: 'Animal teeth drilled with tiny holes and worn as a pendant. The predators that threatened these people were transformed into adornments, fear conquered through art.',
    order: 19,
    artifactId: 'gc_tooth_pendant',
  },
  {
    id: 'gc_frag_20',
    siteId: 'genesis_cavern',
    text: 'The cavern entrance was deliberately narrowed with stacked stones, creating a threshold. To enter was to cross from the wild world into a curated space. Architecture begins here, with the first doorway.',
    order: 20,
    artifactId: 'gc_entrance_stones',
  },
  {
    id: 'gc_frag_21',
    siteId: 'genesis_cavern',
    text: 'Charcoal from the hearth has been dated and redated by three independent labs. The results are consistent and staggering: organized habitation, art, and ritual, all far older than any textbook allows.',
    order: 21,
    artifactId: 'gc_charcoal_sample',
  },
  {
    id: 'gc_frag_22',
    siteId: 'genesis_cavern',
    text: 'A flat stone used as a palette, still bearing traces of five different pigment colors mixed from minerals. Proto-humans didn\'t just paint; they chose colors with purpose and mixed them with skill.',
    order: 22,
    artifactId: 'gc_paint_palette',
  },
  {
    id: 'gc_frag_23',
    siteId: 'genesis_cavern',
    text: 'The final layer of the cavern floor reveals the oldest occupation level: simple tools, a fire pit, and a single smooth stone placed with obvious care. Even at the very beginning, there was intention.',
    order: 23,
    artifactId: 'gc_smooth_stone',
  },
  {
    id: 'gc_frag_24',
    siteId: 'genesis_cavern',
    text: 'Everything in this cavern tells one story: consciousness did not arrive suddenly. It grew, layer by layer, painting by painting, burial by burial. We became human not in a moment but across millennia of small, beautiful acts.',
    order: 24,
    artifactId: 'gc_final_layer',
  },
  {
    id: 'gc_frag_25',
    siteId: 'genesis_cavern',
    text: 'A collection of rounded river stones, each a different color, arranged in a circle within the gallery. No practical purpose. Pure arrangement, pure aesthetics. The first art collection, curated by the first curator.',
    order: 25,
    artifactId: 'gc_stone_collection',
  },
];


// ── Theories for all 10 dig sites ──

export const THEORIES: TheoryDef[] = [

  // ── Dusty Creek ──
  {
    id: 'dc_theory_01',
    siteId: 'dusty_creek',
    question: 'What was the primary function of the Dusty Creek settlement?',
    options: [
      'A permanent agricultural village',
      'A seasonal trading hub connecting distant tribes',
      'A military outpost defending territorial borders',
      'A religious pilgrimage site for harvest ceremonies',
    ],
    correctIndex: 1,
    requiredFragments: 0.4,
    repReward: 5,
    loreText: 'Dusty Creek sat at the intersection of ancient trade routes, and each autumn, tribes from across the continent gathered here to exchange goods, stories, and traditions. The master potter\'s work served as both art and currency in this vast network.',
  },
  {
    id: 'dc_theory_02',
    siteId: 'dusty_creek',
    question: 'What made the master potter\'s work significant beyond artistic merit?',
    options: [
      'The pottery contained encoded astronomical data',
      'The potter\'s designs unified visual traditions from multiple tribes',
      'The vessels were used exclusively for sacred rituals',
      'The potter invented a new firing technique that spread continent-wide',
    ],
    correctIndex: 1,
    requiredFragments: 0.6,
    repReward: 8,
    loreText: 'By blending design elements from plains, desert, coastal, and mountain peoples into a single artistic tradition, the potter created a visual language of unity. Tribes that had never met recognized the shared motifs and trusted traders who carried these works.',
  },
  {
    id: 'dc_theory_03',
    siteId: 'dusty_creek',
    question: 'How did the trade network at Dusty Creek ultimately benefit the connected tribes?',
    options: [
      'It created a military alliance that conquered neighboring peoples',
      'It enabled cultural exchange that enriched every participating group',
      'It concentrated wealth in a single ruling family',
      'It established a common written language across the continent',
    ],
    correctIndex: 1,
    requiredFragments: 0.8,
    repReward: 12,
    loreText: 'The trade network facilitated the exchange of crops, techniques, music, and stories across thousands of miles. Tribes gained access to obsidian, turquoise, copper, shells, and new agricultural knowledge, enriching their cultures far beyond what isolation could achieve.',
  },

  // ── Millbrook Farm ──
  {
    id: 'mf_theory_01',
    siteId: 'millbrook_farm',
    question: 'What was unusual about the Roman villa at Millbrook Farm?',
    options: [
      'It was built using exclusively local British construction methods',
      'It served as a secret meeting place for a Roman-Briton resistance',
      'It was the largest Roman villa ever found in Britain',
      'It was abandoned peacefully when the legions withdrew',
    ],
    correctIndex: 1,
    requiredFragments: 0.4,
    repReward: 5,
    loreText: 'Behind the facade of a loyal Roman nobleman\'s estate, the villa concealed hidden rooms, stockpiled weapons, and coded communications. The owner risked everything to bridge the divide between occupier and occupied.',
  },
  {
    id: 'mf_theory_02',
    siteId: 'millbrook_farm',
    question: 'What ultimately happened to the Roman nobleman who aided the Britons?',
    options: [
      'He was promoted to governor for his diplomatic skills',
      'He escaped to Gaul with his British allies',
      'He was discovered and the villa was destroyed, but he warned the Britons first',
      'He lived peacefully to old age, his secret never revealed',
    ],
    correctIndex: 2,
    requiredFragments: 0.7,
    repReward: 10,
    loreText: 'When Roman authorities discovered the betrayal, they sent soldiers to raze the villa. But the nobleman had enough warning to alert the Briton leaders. The villa burned, but the people he protected survived to resist another day.',
  },
  {
    id: 'mf_theory_03',
    siteId: 'millbrook_farm',
    question: 'What does the coded communication found in the villa suggest about the resistance?',
    options: [
      'It was a spontaneous uprising with no planning',
      'It was organized by the Briton druids without Roman help',
      'It was a sophisticated network with meetings, weapon caches, and encrypted messages',
      'It was a Roman military operation disguised as resistance',
    ],
    correctIndex: 2,
    requiredFragments: 0.6,
    repReward: 8,
    loreText: 'The wax tablets with dual-language coding, the hidden weapon caches, and the organized meeting schedules reveal a resistance movement of remarkable sophistication. The nobleman used his education and resources to build an intelligence network that operated under Rome\'s nose.',
  },

  // ── Sunken Temple ──
  {
    id: 'st_theory_01',
    siteId: 'sunken_temple',
    question: 'What was the primary purpose of the Mayan temple before it sank?',
    options: [
      'A royal palace for the city\'s ruling dynasty',
      'A marketplace for jade and obsidian trade',
      'An astronomical observatory that also tracked seismic activity',
      'A military fortress guarding the city\'s water supply',
    ],
    correctIndex: 2,
    requiredFragments: 0.4,
    repReward: 6,
    loreText: 'The temple housed a school of astronomers led by Ahau K\'in, who used obsidian mirrors, calendar stones, and careful observation to track both celestial and seismic patterns. Their data was remarkably accurate.',
  },
  {
    id: 'st_theory_02',
    siteId: 'sunken_temple',
    question: 'Why weren\'t the astronomers\' warnings heeded before the earthquake?',
    options: [
      'The astronomers kept their findings secret',
      'The king and high priest refused to act on the predictions',
      'The warning came too late for evacuation',
      'The population didn\'t understand the scientific language used',
    ],
    correctIndex: 1,
    requiredFragments: 0.6,
    repReward: 8,
    loreText: 'Ahau K\'in petitioned the king three times with precise calculations showing the coming disaster. The high priest opposed him, viewing the predictions as blasphemy against the gods. Political power trumped scientific evidence, a pattern as old as civilization.',
  },
  {
    id: 'st_theory_03',
    siteId: 'sunken_temple',
    question: 'What did Ahau K\'in do when he realized the city would not evacuate?',
    options: [
      'He fled with his family to higher ground',
      'He destroyed his research to prevent it falling into enemy hands',
      'He sealed his life\'s work in the temple and stayed to the end',
      'He led a rebellion against the king',
    ],
    correctIndex: 2,
    requiredFragments: 0.8,
    repReward: 12,
    loreText: 'Knowing the earthquake was imminent, Ahau K\'in sealed his instruments, calculations, and records in the temple\'s highest chamber. His skeleton was found in the inner sanctum wearing a jade death mask, surrounded by the tools of his trade. He chose to die with his observatory.',
  },
  {
    id: 'st_theory_04',
    siteId: 'sunken_temple',
    question: 'What does the survival of some families suggest about the astronomers\' influence?',
    options: [
      'The astronomers had no influence beyond the temple walls',
      'Some common people trusted the astronomers and fled before the earthquake',
      'The king secretly organized a partial evacuation',
      'Survival was purely coincidental',
    ],
    correctIndex: 1,
    requiredFragments: 0.5,
    repReward: 7,
    loreText: 'Clay figurines depicting fleeing families, and the absence of certain household goods from the ruins, suggest that some inhabitants believed Ahau K\'in and left before the earthquake struck. The astronomers saved lives even as the powerful dismissed them.',
  },

  // ── Silk Road ──
  {
    id: 'sr_theory_01',
    siteId: 'silk_road',
    question: 'Who was "the Bridge" of the Silk Road waystation?',
    options: [
      'A Chinese imperial envoy stationed along the trade route',
      'A legendary merchant who brokered peace and facilitated multi-cultural trade',
      'A mythical figure invented by later storytellers',
      'A Sogdian warlord who taxed passing caravans',
    ],
    correctIndex: 1,
    requiredFragments: 0.4,
    repReward: 6,
    loreText: 'The Bridge was a real merchant whose personal seal has been found on contracts from Xi\'an to Constantinople. By creating standardized weights, mediating disputes, and building an oasis waystation, this remarkable individual made the Silk Road safer and more prosperous for everyone.',
  },
  {
    id: 'sr_theory_02',
    siteId: 'silk_road',
    question: 'How did the Bridge broker peace between warring factions?',
    options: [
      'By threatening military force with a private army',
      'By paying bribes to both sides',
      'By mediating disputes through diplomacy, shared trade, and ritual treaty-making',
      'By appealing to a higher religious authority',
    ],
    correctIndex: 2,
    requiredFragments: 0.6,
    repReward: 8,
    loreText: 'Evidence of ceremonially broken daggers, multi-language peace treaties, and shared feasting vessels shows that the Bridge used a combination of personal diplomacy, economic incentive, and ritual ceremony to resolve conflicts. Trade was the common language that made peace profitable.',
  },
  {
    id: 'sr_theory_03',
    siteId: 'silk_road',
    question: 'What was the Bridge\'s lasting legacy after their death?',
    options: [
      'The waystation was abandoned and forgotten',
      'The waystation continued operating for two centuries, maintained by the Bridge\'s descendants',
      'A war destroyed the waystation within a year',
      'The Bridge\'s wealth was stolen by bandits',
    ],
    correctIndex: 1,
    requiredFragments: 0.8,
    repReward: 12,
    loreText: 'The Bridge\'s final testament instructed their children to maintain the waystation and never turn away a traveler. The stone marker at the gate outlasted its founder by generations, and the waystation remained a beacon of commerce and tolerance along the Silk Road for two centuries.',
  },

  // ── Frozen Cairn ──
  {
    id: 'fc_theory_01',
    siteId: 'frozen_cairn',
    question: 'Who was Sigrid Raven-tongue?',
    options: [
      'A Viking queen who ruled through political marriage',
      'A shieldmaiden warrior-poet who defended her people and composed sagas',
      'A Norse priestess devoted to the worship of Freyja',
      'A merchant who established trade routes to Iceland',
    ],
    correctIndex: 1,
    requiredFragments: 0.4,
    repReward: 6,
    loreText: 'Sigrid was that rarest combination: a warrior of proven courage and a poet of lasting power. Her rune stones describe battles won by sword and morale raised by verse. She fought, she sang, and her people loved her for both.',
  },
  {
    id: 'fc_theory_02',
    siteId: 'frozen_cairn',
    question: 'What was Sigrid\'s most famous exploit?',
    options: [
      'Raiding a monastery in England for its gold',
      'Discovering a new trade route to Byzantium',
      'Holding a river ford alone while her people retreated, composing a death-song as she fought',
      'Negotiating a peace treaty with the Danish king',
    ],
    correctIndex: 2,
    requiredFragments: 0.6,
    repReward: 8,
    loreText: 'The rune stones describe Sigrid standing alone at a river crossing for an hour, fighting off attackers while her village evacuated. She sang her verses as she fought, and her people carved every word into stone after the battle. She survived, but the wound in her shoulder marked her for life.',
  },
  {
    id: 'fc_theory_03',
    siteId: 'frozen_cairn',
    question: 'What do Sigrid\'s burial goods reveal about Viking culture?',
    options: [
      'That Vikings valued only martial prowess',
      'That women held no status in Viking society',
      'That artistic and intellectual achievement was honored alongside warrior skill',
      'That burial goods were chosen at random',
    ],
    correctIndex: 2,
    requiredFragments: 0.8,
    repReward: 12,
    loreText: 'Sigrid was buried with sword, shield, and ship, but also with a stylus and blank rune staves for composing in the afterlife. Her people valued her voice as much as her blade, revealing a Viking culture far more nuanced than the raider stereotype suggests.',
  },

  // ── Pharaoh's Shadow ──
  {
    id: 'ps_theory_01',
    siteId: 'pharaohs_shadow',
    question: 'Why is the pharaoh associated with this tomb called "forgotten"?',
    options: [
      'The tomb was too small to be noticed by explorers',
      'The pharaoh\'s successor deliberately erased his name from all records',
      'The pharaoh died in infancy before accomplishing anything',
      'Natural erosion destroyed all identifying inscriptions',
    ],
    correctIndex: 1,
    requiredFragments: 0.4,
    repReward: 6,
    loreText: 'Hieroglyphic evidence shows that the pharaoh\'s cartouche was systematically chiseled from every surface after his death. His successor enacted a damnatio memoriae, erasing the pharaoh from history. Only Khentkaus, loyal to the end, preserved the truth in shadow.',
  },
  {
    id: 'ps_theory_02',
    siteId: 'pharaohs_shadow',
    question: 'What knowledge did Khentkaus preserve in the hidden chamber?',
    options: [
      'Military secrets and battle plans',
      'The pharaoh\'s personal love letters',
      'Advanced mathematics, medicine, astronomy, and engineering from multiple civilizations',
      'Religious texts praising the forgotten pharaoh',
    ],
    correctIndex: 2,
    requiredFragments: 0.6,
    repReward: 10,
    loreText: 'Khentkaus amassed scrolls covering medicine, engineering, agriculture, astronomy, and mathematics, drawing from Egyptian, Nubian, and foreign sources. Many texts contain knowledge that wouldn\'t be rediscovered for centuries. The adviser saw knowledge itself as sacred and worth any risk to preserve.',
  },
  {
    id: 'ps_theory_03',
    siteId: 'pharaohs_shadow',
    question: 'What was Khentkaus\'s relationship with the priesthood?',
    options: [
      'Khentkaus was a high priest who used religious authority to protect the knowledge',
      'Khentkaus was tried for heresy but acquitted by the pharaoh, and continued working in defiance of the priests',
      'Khentkaus worked secretly with the priests to hide the scrolls',
      'The priesthood was unaware of Khentkaus\'s activities',
    ],
    correctIndex: 1,
    requiredFragments: 0.5,
    repReward: 8,
    loreText: 'The hieroglyphic tablets record that Khentkaus was accused of "speaking the words that unmake the gods" by priests who saw scientific knowledge as a threat to religious authority. The pharaoh personally acquitted the adviser, but the conflict between knowledge and dogma defined Khentkaus\'s entire career.',
  },
  {
    id: 'ps_theory_04',
    siteId: 'pharaohs_shadow',
    question: 'What was Khentkaus\'s ultimate motivation?',
    options: [
      'Personal glory and recognition by future generations',
      'Revenge against the successor who erased the pharaoh',
      'A belief that knowledge is immortal and must be preserved at any cost',
      'Obedience to a divine commandment from Thoth',
    ],
    correctIndex: 2,
    requiredFragments: 0.8,
    repReward: 12,
    loreText: 'Khentkaus\'s personal testament reveals a mind devoted to the principle that truth transcends politics and power. The adviser spent years copying forbidden texts, building hidden chambers, and creating mathematical puzzles to protect the knowledge. The ouroboros seal symbolized the adviser\'s belief in eternal cycles of discovery.',
  },

  // ── Obsidian Forge ──
  {
    id: 'of_theory_01',
    siteId: 'obsidian_forge',
    question: 'What was master smith Kanemitsu\'s secret activity?',
    options: [
      'Forging counterfeit coins for personal enrichment',
      'Hiding messages about an impending betrayal in the blades he forged',
      'Building weapons for a foreign invasion force',
      'Developing gunpowder weapons before they reached Japan',
    ],
    correctIndex: 1,
    requiredFragments: 0.4,
    repReward: 6,
    loreText: 'Kanemitsu discovered that a powerful daimyo was plotting to betray the emperor. Unable to speak openly without being killed, the smith etched microscopic warnings into the hamon lines of every blade he forged, knowing that other smiths would eventually notice the hidden characters.',
  },
  {
    id: 'of_theory_02',
    siteId: 'obsidian_forge',
    question: 'How did Kanemitsu gather intelligence about the conspiracy?',
    options: [
      'He tortured captured soldiers for information',
      'He used his tea ceremonies and travels collecting iron sand to build an intelligence network',
      'He intercepted carrier pigeons',
      'A supernatural fox spirit revealed the plot to him',
    ],
    correctIndex: 1,
    requiredFragments: 0.6,
    repReward: 8,
    loreText: 'Kanemitsu\'s tea ceremonies brought samurai together in an atmosphere of trust, where they spoke freely. His journeys across Japan to collect the finest iron sand gave him access to information from every province. The ceramic fox figurines scattered through the forge were his private emblem: the trickster hiding in plain sight.',
  },
  {
    id: 'of_theory_03',
    siteId: 'obsidian_forge',
    question: 'What happened when Kanemitsu\'s warnings finally reached the emperor?',
    options: [
      'The emperor ignored the warnings and the betrayal succeeded',
      'Kanemitsu was executed as a liar',
      'The betrayal was thwarted, the conspirators arrested, and Kanemitsu was honored posthumously',
      'The daimyo surrendered peacefully',
    ],
    correctIndex: 2,
    requiredFragments: 0.8,
    repReward: 12,
    loreText: 'The daimyo discovered Kanemitsu\'s activities and burned the forge to the ground, but the smith\'s warnings had already reached loyalist hands. The conspiracy was exposed, the traitors arrested, and the empire preserved. Kanemitsu died in the fire but was honored with a stone memorial that still stands among the ruins.',
  },
  {
    id: 'of_theory_04',
    siteId: 'obsidian_forge',
    question: 'What does Kanemitsu\'s story reveal about the role of craftspeople in feudal Japan?',
    options: [
      'Craftspeople had no political influence whatsoever',
      'Smiths were feared as sorcerers and avoided by the ruling class',
      'A master smith could wield enormous influence through the trust placed in their craft',
      'Craftspeople were part of the formal military hierarchy',
    ],
    correctIndex: 2,
    requiredFragments: 0.5,
    repReward: 7,
    loreText: 'Kanemitsu\'s story upends the rigid social hierarchy of feudal Japan. A craftsman, technically of lower status than the samurai he served, saved the empire through intelligence, artistry, and courage. The trust that warriors placed in their swordsmith gave Kanemitsu access that no spy could have achieved.',
  },

  // ── Atlantean Shelf ──
  {
    id: 'as_theory_01',
    siteId: 'atlantean_shelf',
    question: 'What level of technology did the sunken city possess?',
    options: [
      'Primitive fishing village technology',
      'Standard ancient Mediterranean technology',
      'Remarkably advanced engineering including tidal prediction, central heating, and precision optics',
      'Technology equivalent to the Industrial Revolution',
    ],
    correctIndex: 2,
    requiredFragments: 0.4,
    repReward: 7,
    loreText: 'The city\'s engineers built bronze mechanisms for predicting tides and earthquakes, glass lenses for astronomical observation, heated water systems, and standardized infrastructure. Their technology rivaled or exceeded anything else in the ancient Mediterranean, making the city\'s destruction all the more tragic.',
  },
  {
    id: 'as_theory_02',
    siteId: 'atlantean_shelf',
    question: 'What caused the city\'s destruction?',
    options: [
      'A volcanic eruption buried it in ash',
      'An invading army conquered and razed it',
      'The merchant council weakened the seawall to expand the harbor, and the sea flooded the city',
      'A plague killed the population',
    ],
    correctIndex: 2,
    requiredFragments: 0.6,
    repReward: 10,
    loreText: 'Council tablets record the fatal decision in detail. Engineers warned that expanding the harbor would compromise the seawall. The merchant council, driven by profits, overruled them. When the seawall breached, the sea entered through the expanded harbor exactly as predicted. Greed literally drowned the city.',
  },
  {
    id: 'as_theory_03',
    siteId: 'atlantean_shelf',
    question: 'What is the central lesson of the Atlantean city\'s story?',
    options: [
      'Maritime civilizations are inherently unstable',
      'Advanced technology inevitably leads to destruction',
      'A society that ignores its own experts in pursuit of profit engineers its own downfall',
      'The ancient world lacked the knowledge to build lasting coastal cities',
    ],
    correctIndex: 2,
    requiredFragments: 0.8,
    repReward: 15,
    loreText: 'The city had the knowledge to survive. Its engineers understood the risks. But the merchant council chose short-term profit over long-term survival, ignored expert warnings, and cut corners on critical infrastructure. The city\'s epitaph could serve as a warning for any age: "We are the masters of wave and stone." The sea disagreed.',
  },

  // ── Library of Sands ──
  {
    id: 'ls_theory_01',
    siteId: 'library_of_sands',
    question: 'What kind of civilization built the Library of the Sands?',
    options: [
      'A nomadic herding culture that left few permanent structures',
      'An advanced society with sophisticated writing, science, and engineering, unknown to history',
      'An Egyptian colony in the Sahara',
      'A medieval Islamic university',
    ],
    correctIndex: 1,
    requiredFragments: 0.4,
    repReward: 8,
    loreText: 'The Library of the Sands belongs to a civilization that left no other known record. Their spiral script, precision instruments, micro-inscription technology, and cataloging systems suggest a culture obsessed with preserving and transmitting knowledge, far more advanced than any contemporary society we know of.',
  },
  {
    id: 'ls_theory_02',
    siteId: 'library_of_sands',
    question: 'Why did the civilization that built the library vanish?',
    options: [
      'They were conquered by a more powerful empire',
      'A plague wiped out the population',
      'Their climate changed from fertile grassland to desert over decades, and they could not survive the drought',
      'They migrated to a coastal city and assimilated',
    ],
    correctIndex: 2,
    requiredFragments: 0.6,
    repReward: 10,
    loreText: 'Climate tablets with meticulous rainfall data show a thirty-year decline from adequate precipitation to zero. The mosaic floor depicting their library surrounded by rivers and grassland confirms that the Sahara was once green. They watched their world dry up and chose to preserve their knowledge rather than fight the inevitable.',
  },
  {
    id: 'ls_theory_03',
    siteId: 'library_of_sands',
    question: 'What was the purpose of the library\'s final message?',
    options: [
      'A religious prophecy about the end of the world',
      'A curse upon anyone who disturbed the library',
      'A warning to future civilizations to adapt to environmental change or preserve knowledge for those who come after',
      'A historical chronicle of the civilization\'s kings',
    ],
    correctIndex: 2,
    requiredFragments: 0.8,
    repReward: 15,
    loreText: 'The final tablet reads: "We perished because the world changed and we could not change it back. Learn from this. Adapt, or leave a library for those who come after." It is a message of stunning clarity and humility, a civilization facing extinction with the wisdom to think beyond itself.',
  },
  {
    id: 'ls_theory_04',
    siteId: 'library_of_sands',
    question: 'What does the network of silver compasses suggest about the library?',
    options: [
      'The library was a military command center with outposts',
      'Multiple sites across the region were designed to guide seekers to the library',
      'The compasses were trade goods with no special significance',
      'The civilization worshipped magnetic forces',
    ],
    correctIndex: 1,
    requiredFragments: 0.5,
    repReward: 8,
    loreText: 'Silver compasses found at distant sites all point toward the library\'s central chamber, suggesting the civilization created a network of wayfinding devices. They wanted the library to be found, but only by those with the curiosity and determination to follow the trail. It was a test of worthiness built into the landscape itself.',
  },

  // ── Genesis Cavern ──
  {
    id: 'gc_theory_01',
    siteId: 'genesis_cavern',
    question: 'What makes the Genesis Cavern revolutionary for our understanding of human origins?',
    options: [
      'It contains the largest collection of stone tools ever found',
      'It proves that art, music, ritual burial, and abstract thought existed far earlier than previously believed',
      'It shows that proto-humans were primarily hunters, not gatherers',
      'It demonstrates that modern humans evolved in this specific cave',
    ],
    correctIndex: 1,
    requiredFragments: 0.4,
    repReward: 10,
    loreText: 'The cavern contains cave art, bone flutes, ritual burials, decorative objects, and a dedicated gallery space, all dating to an era when proto-humans were believed capable of only basic survival activities. Consciousness, creativity, and culture emerged far earlier than any textbook suggests.',
  },
  {
    id: 'gc_theory_02',
    siteId: 'genesis_cavern',
    question: 'What does the dedicated gallery chamber reveal about proto-human cognition?',
    options: [
      'That proto-humans feared the dark and avoided deep caves',
      'That they could distinguish between functional and sacred spaces, creating a room devoted entirely to art',
      'That the gallery was used for food storage',
      'That it was a natural formation with no human modification',
    ],
    correctIndex: 1,
    requiredFragments: 0.6,
    repReward: 12,
    loreText: 'The gallery contains no tools, no food remains, no sleeping areas, nothing functional. It exists solely for art: paintings, arranged stones, and a quartz crystal that scatters light across the walls. The ability to set aside a space for purely aesthetic and possibly spiritual purposes implies a level of abstract thinking we never associated with this era.',
  },
  {
    id: 'gc_theory_03',
    siteId: 'genesis_cavern',
    question: 'What does the presence of teaching arrangements suggest?',
    options: [
      'That one individual hoarded all tool-making knowledge',
      'That tool-making was instinctive and required no instruction',
      'That knowledge was deliberately transmitted from skilled individuals to learners across generations',
      'That the tools were arranged by natural geological processes',
    ],
    correctIndex: 2,
    requiredFragments: 0.7,
    repReward: 12,
    loreText: 'Stone tools arranged in clear progression from crude to expert, found in a pattern consistent with teaching, prove that proto-humans engaged in deliberate knowledge transfer. This is education, the foundation of culture and civilization, present at the very dawn of our species.',
  },
  {
    id: 'gc_theory_04',
    siteId: 'genesis_cavern',
    question: 'What is the overarching narrative of the Genesis Cavern?',
    options: [
      'That early humans were violent and territorial',
      'That consciousness emerged suddenly in a single evolutionary leap',
      'That humanity became human gradually through accumulated acts of creativity, care, and curiosity over millennia',
      'That proto-humans were indistinguishable from modern humans',
    ],
    correctIndex: 2,
    requiredFragments: 0.9,
    repReward: 20,
    loreText: 'Every layer of the cavern tells the same story: consciousness did not arrive in a flash. It grew, painting by painting, burial by burial, song by song. The first doorway, the first necklace, the first flute, the first funeral, each was a small step in the longest journey our species has ever taken. We became human not in a moment but across millennia of small, beautiful acts.',
  },
];
